/*───────────────────────────────────────────────────────────────
 📄 File: ConferenceCallerControlsTab.tsx
 🧩 Description:
    "Caller Controls" tab content for ConferenceProfileFormView.
    Contains all DTMF digit bindings for mute, volume, energy,
    and hangup controls.
────────────────────────────────────────────────────────────────*/

import { Settings } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { ConferenceProfileSettings } from '../../conferenceProfiles/types';

interface ConferenceCallerControlsTabProps {
  settings: ConferenceProfileSettings;
  onSettingsChange: <K extends keyof ConferenceProfileSettings>(field: K, value: ConferenceProfileSettings[K]) => void;
  t: (key: string, fallback?: string) => string;
}

export function ConferenceCallerControlsTab({ settings, onSettingsChange, t }: ConferenceCallerControlsTabProps) {
  return (
    <div className="space-y-4">
      <FormBox title={t('conferenceProfiles:tabs.callerControls')} icon={<Settings className="h-5 w-5" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">

          {/* Mute Digits */}
          <SimpleTooltip content={t('conferenceProfiles:fields.muteDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.muteDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.muteDigits} onChange={(e) => onSettingsChange('muteDigits', e.target.value)} placeholder="0" className="w-20" />

          {/* Deaf Mute */}
          <SimpleTooltip content={t('conferenceProfiles:fields.deafMuteDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.deafMuteDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.deafMuteDigits} onChange={(e) => onSettingsChange('deafMuteDigits', e.target.value)} placeholder="*" className="w-20" />

          {/* Energy Up */}
          <SimpleTooltip content={t('conferenceProfiles:fields.energyUpDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.energyUpDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.energyUpDigits} onChange={(e) => onSettingsChange('energyUpDigits', e.target.value)} placeholder="9" className="w-20" />

          {/* Energy Equal */}
          <SimpleTooltip content={t('conferenceProfiles:fields.energyEquDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.energyEquDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.energyEquDigits} onChange={(e) => onSettingsChange('energyEquDigits', e.target.value)} placeholder="8" className="w-20" />

          {/* Energy Down */}
          <SimpleTooltip content={t('conferenceProfiles:fields.energyDnDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.energyDnDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.energyDnDigits} onChange={(e) => onSettingsChange('energyDnDigits', e.target.value)} placeholder="7" className="w-20" />

          {/* Volume Talk Up */}
          <SimpleTooltip content={t('conferenceProfiles:fields.volTalkUpDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.volTalkUpDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.volTalkUpDigits} onChange={(e) => onSettingsChange('volTalkUpDigits', e.target.value)} placeholder="3" className="w-20" />

          {/* Volume Talk Zero */}
          <SimpleTooltip content={t('conferenceProfiles:fields.volTalkZeroDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.volTalkZeroDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.volTalkZeroDigits} onChange={(e) => onSettingsChange('volTalkZeroDigits', e.target.value)} placeholder="2" className="w-20" />

          {/* Volume Talk Down */}
          <SimpleTooltip content={t('conferenceProfiles:fields.volTalkDnDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.volTalkDnDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.volTalkDnDigits} onChange={(e) => onSettingsChange('volTalkDnDigits', e.target.value)} placeholder="1" className="w-20" />

          {/* Volume Listen Up */}
          <SimpleTooltip content={t('conferenceProfiles:fields.volListenUpDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.volListenUpDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.volListenUpDigits} onChange={(e) => onSettingsChange('volListenUpDigits', e.target.value)} placeholder="6" className="w-20" />

          {/* Volume Listen Zero */}
          <SimpleTooltip content={t('conferenceProfiles:fields.volListenZeroDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.volListenZeroDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.volListenZeroDigits} onChange={(e) => onSettingsChange('volListenZeroDigits', e.target.value)} placeholder="5" className="w-20" />

          {/* Volume Listen Down */}
          <SimpleTooltip content={t('conferenceProfiles:fields.volListenDnDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.volListenDnDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.volListenDnDigits} onChange={(e) => onSettingsChange('volListenDnDigits', e.target.value)} placeholder="4" className="w-20" />

          {/* Hangup Digits */}
          <SimpleTooltip content={t('conferenceProfiles:fields.hangupDigits.tooltip')} position="top" maxWidth="300px">
            <Label className="text-left">{t('conferenceProfiles:fields.hangupDigits.label')}</Label>
          </SimpleTooltip>
          <Input value={settings.hangupDigits} onChange={(e) => onSettingsChange('hangupDigits', e.target.value)} placeholder="#" className="w-20" />

        </div>
      </FormBox>
    </div>
  );
}
